// src/hooks/useTeam.ts
//
// React data hook for the Settings → Team screen (Story 1.4). Plain
// useState/useCallback/useEffect per the repo's hook style (useRegions is the
// reference) — no React Query. All data access goes through teamService.
// Mutations refetch rather than update optimistically; a failed background
// refresh after a successful mutation is swallowed (it must not raise the
// page-level error banner and bait a re-submit — the 1.1 finding).

import { useState, useEffect, useCallback } from 'react'
import { teamService } from '../services/teamService'
import type { YakTeamMember, YakUserAttribute } from '../types/yak'
import type { YakUserAttributeType } from '../types/yak'

interface UseTeamReturn {
  members: YakTeamMember[]
  attributes: YakUserAttribute[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  createUser: (input: {
    email: string
    first_name: string
    last_name: string
    position?: string | null
  }) => Promise<void>
  updatePosition: (userId: string, position: string | null) => Promise<void>
  addAttribute: (
    userId: string,
    type: YakUserAttributeType,
    value: string
  ) => Promise<void>
  removeAttribute: (attributeId: string) => Promise<void>
  deactivate: (userId: string) => Promise<number>
  reactivate: (userId: string) => Promise<void>
}

export function useTeam(): UseTeamReturn {
  const [members, setMembers] = useState<YakTeamMember[]>([])
  const [attributes, setAttributes] = useState<YakUserAttribute[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchTeam = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      setError(null)
      const [nextMembers, nextAttributes] = await Promise.all([
        teamService.listTeam(),
        teamService.listAttributes(),
      ])
      setMembers(nextMembers)
      setAttributes(nextAttributes)
    } catch (err) {
      // A silent refresh runs after a mutation that already succeeded on the
      // server. Surfacing its failure as the page-level banner would
      // contradict that success and bait a re-submit. Only the initial/explicit
      // load owns the banner; a failed background refresh leaves the last-known
      // data in place and reconciles on the next successful fetch.
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load the team')
      } else {
        console.error('Team list refresh failed', err)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTeam()
  }, [fetchTeam])

  const createUser = useCallback(
    async (input: {
      email: string
      first_name: string
      last_name: string
      position?: string | null
    }) => {
      await teamService.createUser(input)
      await fetchTeam(true)
    },
    [fetchTeam]
  )

  const updatePosition = useCallback(
    async (userId: string, position: string | null) => {
      await teamService.updatePosition(userId, position)
      await fetchTeam(true)
    },
    [fetchTeam]
  )

  const addAttribute = useCallback(
    async (userId: string, type: YakUserAttributeType, value: string) => {
      await teamService.addAttribute(userId, type, value)
      await fetchTeam(true)
    },
    [fetchTeam]
  )

  const removeAttribute = useCallback(
    async (attributeId: string) => {
      await teamService.removeAttribute(attributeId)
      await fetchTeam(true)
    },
    [fetchTeam]
  )

  const deactivate = useCallback(
    async (userId: string): Promise<number> => {
      const reassignedCount = await teamService.deactivateUser(userId)
      await fetchTeam(true)
      return reassignedCount
    },
    [fetchTeam]
  )

  const reactivate = useCallback(
    async (userId: string) => {
      await teamService.reactivateUser(userId)
      await fetchTeam(true)
    },
    [fetchTeam]
  )

  return {
    members,
    attributes,
    loading,
    error,
    refetch: () => fetchTeam(),
    createUser,
    updatePosition,
    addAttribute,
    removeAttribute,
    deactivate,
    reactivate,
  }
}
